import React, { useReducer, useEffect, useState } from 'react';
import {
  Box,
  TextField,
  Stack,
  Button,
  Typography,
  Collapse,
  Autocomplete,
  Chip,
} from '@mui/material';
import type { ServiceOffering } from '../../../types/backend';
import type { ServiceOfferingCreateRequest, ServiceOfferingUpdateRequest } from '../services/serviceOfferingsService';
import { useServiceOfferingsService } from '../services/serviceOfferingsService';
import { CATEGORY_LABELS } from '../../../shared/labels';

const CATEGORY_OPTIONS = Object.keys(CATEGORY_LABELS);

function slugify(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 64);
}

interface OfferingFormData {
  name: string;
  display_name: string;
  description: string;
  owner: string;
  category: string;
  customer_description: string;
  cloud_platforms: string;
  sla_tier: string;
}

const EMPTY_FORM: OfferingFormData = {
  name: '',
  display_name: '',
  description: '',
  owner: '',
  category: '',
  customer_description: '',
  cloud_platforms: 'aws',
  sla_tier: '',
};

function offeringToForm(offering: ServiceOffering): OfferingFormData {
  return {
    name: offering.name,
    display_name: offering.display_name,
    description: offering.description ?? '',
    owner: offering.owner ?? '',
    category: offering.category ?? '',
    customer_description: offering.customer_description ?? '',
    cloud_platforms: (offering.cloud_platforms ?? []).join(', '),
    sla_tier: offering.sla_tier ?? '',
  };
}

type FormAction =
  | { field: keyof OfferingFormData; value: string }
  | { type: 'SET_DISPLAY_NAME'; value: string; deriveName: boolean };

function formReducer(state: OfferingFormData, action: FormAction): OfferingFormData {
  if ('type' in action && action.type === 'SET_DISPLAY_NAME') {
    return {
      ...state,
      display_name: action.value,
      name: action.deriveName ? slugify(action.value) : state.name,
    };
  }
  return { ...state, [(action as { field: keyof OfferingFormData; value: string }).field]: (action as { field: keyof OfferingFormData; value: string }).value };
}

interface ValidationErrors {
  name?: string | null;
  display_name?: string | null;
}

function validate(form: OfferingFormData, isNew: boolean): ValidationErrors {
  const errors: ValidationErrors = {};
  if (isNew && !form.name.trim()) errors.name = 'Slug is required';
  if (isNew && form.name.trim() && !/^[a-z0-9][a-z0-9-]*$/.test(form.name.trim())) {
    errors.name = 'Lowercase letters, numbers, hyphens only';
  }
  if (!form.display_name.trim()) errors.display_name = 'Name is required';
  return errors;
}

interface OfferingFormProps {
  offering?: ServiceOffering;
  isNew: boolean;
  saving: boolean;
  onSave: (data: ServiceOfferingCreateRequest | ServiceOfferingUpdateRequest) => Promise<void>;
  onCancel: () => void;
}

const OfferingForm: React.FC<OfferingFormProps> = ({ offering, isNew, saving, onSave, onCancel }) => {
  const [form, dispatch] = useReducer(formReducer, offering ? offeringToForm(offering) : EMPTY_FORM);
  const [errors, setErrors] = React.useState<ValidationErrors>({});
  const [submitError, setSubmitError] = React.useState<string | null>(null);
  const [showAdvanced, setShowAdvanced] = React.useState(false);
  // Track whether slug has been manually overridden (stops auto-derive)
  const [slugOverridden, setSlugOverridden] = React.useState(!isNew);

  // Bundle picker state
  const [requiresOfferings, setRequiresOfferings] = useState<string[]>(offering?.requires_offerings ?? []);
  const [allOfferings, setAllOfferings] = useState<ServiceOffering[]>([]);

  const service = useServiceOfferingsService();

  useEffect(() => {
    service.listOfferings().then((res) => setAllOfferings(res.service_offerings)).catch(() => {
      // Non-fatal: picker degrades gracefully with empty options
    });
  }, [service]);

  // Exclude self and offerings that already require the current offering (best-effort cycle prevention).
  // Full cycle detection is not possible client-side without the full dependency graph, so the backend
  // validates cycles at publish time and will reject any cycle it finds.
  const currentId = offering?.id;
  const bundleOptions = allOfferings.filter((o) => {
    if (o.id === currentId) return false; // no self-reference
    // Exclude if this offering already requires the one being edited (direct cycle)
    if (currentId && (o.requires_offerings ?? []).includes(currentId)) return false;
    return true;
  });

  const selectedBundleOfferings = bundleOptions.filter((o) => requiresOfferings.includes(o.id));

  const handleDisplayNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch({ type: 'SET_DISPLAY_NAME', value: e.target.value, deriveName: isNew && !slugOverridden });
  };

  const handleSlugChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSlugOverridden(true);
    dispatch({ field: 'name', value: e.target.value });
  };

  const field = (f: keyof OfferingFormData) => (e: React.ChangeEvent<HTMLInputElement>) =>
    dispatch({ field: f, value: e.target.value });

  const handleSave = async () => {
    const errs = validate(form, isNew);
    setErrors(errs);
    if (Object.values(errs).some(Boolean)) return;

    setSubmitError(null);
    try {
      const platforms = form.cloud_platforms.split(',').map(s => s.trim()).filter(Boolean);
      if (isNew) {
        await onSave({
          name: form.name,
          display_name: form.display_name,
          description: form.description || undefined,
          owner: form.owner || undefined,
          category: form.category || undefined,
          customer_description: form.customer_description || undefined,
          cloud_platforms: platforms,
          sla_tier: form.sla_tier || undefined,
          requires_offerings: requiresOfferings.length > 0 ? requiresOfferings : undefined,
        } as ServiceOfferingCreateRequest);
      } else {
        await onSave({
          display_name: form.display_name,
          description: form.description || undefined,
          owner: form.owner || undefined,
          category: form.category || undefined,
          customer_description: form.customer_description || undefined,
          cloud_platforms: platforms,
          sla_tier: form.sla_tier || undefined,
          requires_offerings: requiresOfferings,
        } as ServiceOfferingUpdateRequest);
      }
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Save failed');
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {submitError && (
        <Typography color="error" variant="body2">{submitError}</Typography>
      )}

      <TextField
        label="Name"
        value={form.display_name}
        onChange={handleDisplayNameChange}
        error={!!errors.display_name}
        helperText={errors.display_name}
        size="small"
        required
      />

      <TextField
        label="Description"
        value={form.description}
        onChange={field('description')}
        size="small"
        multiline
        rows={2}
      />

      <TextField
        label="Owner"
        value={form.owner}
        onChange={field('owner')}
        size="small"
        helperText="Team or person responsible"
      />

      <Autocomplete
        freeSolo
        options={CATEGORY_OPTIONS}
        value={form.category}
        onInputChange={(_event, value) => {
          dispatch({ field: 'category', value });
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Category"
            size="small"
          />
        )}
      />

      <TextField
        label="What the customer sees"
        value={form.customer_description}
        onChange={field('customer_description')}
        size="small"
        multiline
        rows={2}
        helperText="Shown to customers in their subscription view"
      />

      <TextField
        label="Cloud Platforms"
        value={form.cloud_platforms}
        onChange={field('cloud_platforms')}
        size="small"
        helperText="Comma-separated (e.g. aws, azure)"
      />

      <TextField
        label="SLA Tier"
        value={form.sla_tier}
        onChange={field('sla_tier')}
        size="small"
      />

      <Autocomplete
        multiple
        size="small"
        options={bundleOptions}
        getOptionLabel={(o) => o.display_name}
        value={selectedBundleOfferings}
        onChange={(_, selected) => setRequiresOfferings(selected.map((o) => o.id))}
        isOptionEqualToValue={(a, b) => a.id === b.id}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => (
            <Chip
              {...getTagProps({ index })}
              key={option.id}
              label={option.display_name}
              size="small"
            />
          ))
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label="Requires these services"
            placeholder="Select services…"
            InputLabelProps={{ shrink: true }}
            helperText="Customers must have these active before subscribing. The backend validates dependency cycles at publish time."
          />
        )}
      />

      {isNew && (
        <Box>
          <Button
            size="small"
            variant="text"
            onClick={() => setShowAdvanced(v => !v)}
            sx={{ px: 0, color: 'text.secondary', textTransform: 'none' }}
          >
            {showAdvanced ? 'Hide advanced' : 'Advanced'}
          </Button>
          <Collapse in={showAdvanced}>
            <Box sx={{ mt: 1 }}>
              <TextField
                label="Slug (technical identifier)"
                value={form.name}
                onChange={handleSlugChange}
                error={!!errors.name}
                helperText={errors.name ?? 'Auto-derived from name. Edit here only to resolve a collision.'}
                size="small"
                fullWidth
              />
            </Box>
          </Collapse>
        </Box>
      )}

      <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 1 }}>
        <Button onClick={onCancel} disabled={saving} size="small">Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          size="small"
        >
          {saving ? 'Saving…' : isNew ? 'Create Offering' : 'Save Changes'}
        </Button>
      </Stack>
    </Box>
  );
};

export default OfferingForm;
